import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { PrescriptionGroup, Prescription } from "common/models/types";
import Input from "components/atoms/Input";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import PrescriptionForm from "./PrescriptionForm";

interface PrescriptionGroupFormProps {
  prescriptionGroup: PrescriptionGroup;
  onUpdatePrescriptionDate: (date: Date) => void;
  onAddPrescriptionItem: () => void;
  onUpdatePrescriptionItem: (prescription: Prescription, index: number) => void;
  onDeletePrescriptionItem: (index: number) => void;
  onSave: () => void;
  onCancel: () => void;
}

const PrescriptionItem = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  background-color: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const PrescriptionGroupForm: React.FC<PrescriptionGroupFormProps> = ({
  prescriptionGroup,
  onUpdatePrescriptionDate,
  onAddPrescriptionItem,
  onUpdatePrescriptionItem,
  onDeletePrescriptionItem,
  onSave,
  onCancel,
}) => {
  return (
    <PrescriptionItem style={{ backgroundColor: COLOR_SYSTEM.get("Blue")?.[50] }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
        <Text textType="Body1" color={COLOR_SYSTEM.get("Blue")?.[700]}>
          {prescriptionGroup.prescriptions.some((p) => p.id) ? "처방전 수정" : "새 처방전 추가"}
        </Text>
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            buttonType="button"
            color="Primary"
            size="Small"
            type="IconWithText"
            variant="Outlined"
            icon="Plus"
            left={true}
            onClick={onAddPrescriptionItem}
          >
            약품 추가
          </Button>
          <Button
            buttonType="button"
            color="Primary"
            size="Small"
            type="IconWithText"
            variant="Contained"
            icon="User Check"
            left={true}
            onClick={onSave}
          >
            저장
          </Button>
          <Button
            buttonType="button"
            color="Negative"
            size="Small"
            type="IconWithText"
            variant="Outlined"
            icon="X"
            left={true}
            onClick={onCancel}
          >
            취소
          </Button>
        </div>
      </div>

      {/* 처방일 입력 필드 */}
      <div style={{ marginBottom: "16px" }}>
        <FormGroup>
          <Input
            label="처방일"
            type="date"
            value={
              prescriptionGroup.prescriptionDate
                ? new Date(prescriptionGroup.prescriptionDate).toISOString().split("T")[0]
                : ""
            }
            onChange={(e) => {
              const newDate = e.target.value ? new Date(e.target.value) : new Date();
              onUpdatePrescriptionDate(newDate);
            }}
          />
        </FormGroup>
      </div>

      {/* 각 처방약 편집 UI */}
      {prescriptionGroup.prescriptions.map((prescription, index) => (
        <PrescriptionForm
          key={prescription.id || `prescription-${index}`}
          prescription={prescription}
          index={index}
          onUpdate={onUpdatePrescriptionItem}
          onDelete={onDeletePrescriptionItem}
        />
      ))}

      {prescriptionGroup.prescriptions.length === 0 && (
        <div style={{ textAlign: "center", padding: "16px" }}>
          <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[500]}>
            약품을 추가해주세요
          </Text>
          <Button
            buttonType="button"
            color="Primary"
            size="Small"
            type="Icon"
            variant="Ghost"
            icon="Plus"
            onClick={onAddPrescriptionItem}
            style={{ marginTop: "8px" }}
          />
        </div>
      )}
    </PrescriptionItem>
  );
};

export default React.memo(PrescriptionGroupForm);
